// Vendors
import {
  TrackingEventsEnum,
  TrackingLinkEventReturnType,
  TrackingViewEventReturnType,
} from '@openbank/ziniaweb-ui-core-models';
// Types
import { TrackBuilderType } from 'types/track-builder.type';
import {
  HandleErrorViewType,
  KYCTrackingHandlersReturnType,
  TrackCheckConsentsHandlerType,
} from './types/kyc-landing.handlers.type';
// Utils
import {
  mapMethodToModalTracking,
  mapMethodToTracking,
} from './utils/kyc-landing.tracking.handlers.utils';

const trackViewHandler = (): TrackingViewEventReturnType => ({
  payload: {
    pageName: '/identification/step_3',
    sectionFamily: 'customer',
    sectionCategory: 'orders',
    sectionName: 'lending',
    interactionType: 'funnel',
    serviceType: 'userSettings',
    processType: 'identification',
    processStep: 'step_3',
  },
  type: TrackingEventsEnum.VIEW,
});

const trackSelectMethodHandler = (method: string): TrackingLinkEventReturnType => ({
  payload: {
    action: 'select',
    format: 'radioButton',
    component: 'form',
    element: mapMethodToTracking(method),
    eventCategory: 'KYC',
    eventAction: 'clickRadioButton',
    eventLabel: mapMethodToTracking(method),
  },
  type: TrackingEventsEnum.LINK,
});

const trackContinueHandler = (method: string): TrackingLinkEventReturnType => ({
  payload: {
    action: 'continue',
    format: 'button',
    component: 'form',
    element: 'continue',
    data: `identificationMethos/radioButton/${mapMethodToTracking(method)}`,
    eventCategory: 'KYC',
    eventAction: 'clickButton',
    eventLabel: `${mapMethodToTracking(method)}continueButton`,
  },
  type: TrackingEventsEnum.LINK,
});

const trackOpenModalHandler = (method: string): TrackingLinkEventReturnType => ({
  payload: {
    action: 'open',
    format: 'modal',
    component: `modalContainer/identifyWith${mapMethodToModalTracking(method)}`,
    element: 'full',
    eventCategory: 'KYC',
    eventAction: `modalIdentifyWith${mapMethodToModalTracking(method)}`,
    eventLabel: 'open',
  },
  type: TrackingEventsEnum.LINK,
});

const trackCloseModalHandler = (method: string): TrackingLinkEventReturnType => ({
  payload: {
    action: 'close',
    format: 'cross',
    component: `modalContainer/identifyWith${mapMethodToModalTracking(method)}`,
    element: 'cross',
    eventCategory: 'KYC',
    eventAction: `modalIdentifyWith${mapMethodToModalTracking(method)}`,
    eventLabel: 'close',
  },
  type: TrackingEventsEnum.LINK,
});

const trackConfirmHandler = (method: string): TrackingLinkEventReturnType => ({
  payload: {
    action: 'continue',
    format: 'button',
    component: `modalContainer/identifyWith${mapMethodToModalTracking(method)}`,
    element: 'continue',
    eventCategory: 'KYC',
    eventAction: `modalIdentifyWith${mapMethodToModalTracking(method)}`,
    eventLabel: 'continueButton',
  },
  type: TrackingEventsEnum.LINK,
});

const trackCheckConsentsHandler = ({
  method,
  checked,
}: TrackCheckConsentsHandlerType): TrackingLinkEventReturnType => ({
  payload: {
    action: checked ? 'check' : 'uncheck',
    format: 'checkBox',
    component: `modalContainer/identifyWith${mapMethodToModalTracking(method)}`,
    element: 'consents',
    eventCategory: 'KYC',
    eventAction: `modalIdentifyWith${mapMethodToModalTracking(method)}`,
    eventLabel: 'consents',
  },
  type: TrackingEventsEnum.LINK,
});

const trackErrorViewHandler = ({
  errorDescription,
  errorStatus,
  errorNumber,
}): TrackingViewEventReturnType => ({
  payload: {
    pageName: '/error/validateIdentity',
    sectionFamily: 'customer',
    sectionCategory: 'orders',
    sectionName: 'lending',
    interactionType: 'page',
    serviceType: 'userSettings',
    errorCategory: 'validateIdentity',
    errorDescription,
    errorStatus,
    errorNumber,
  },
  type: TrackingEventsEnum.VIEW,
});

const KycTrackingHandlers = (track: TrackBuilderType): KYCTrackingHandlersReturnType => ({
  handleTrackView: () => track(trackViewHandler()),
  handleTrackSelectMethod: (method: string) => track(trackSelectMethodHandler(method)),
  handleTrackContinue: (method: string) => track(trackContinueHandler(method)),
  handleTrackOpenModal: (method: string) => track(trackOpenModalHandler(method)),
  handleTrackCloseModal: (method: string) => track(trackCloseModalHandler(method)),
  handleTrackCheckConsents: ({ method, checked }: TrackCheckConsentsHandlerType) =>
    track(trackCheckConsentsHandler({ method, checked })),
  handleTrackConfirm: (method: string) => track(trackConfirmHandler(method)),
  handleErrorView: ({ errorDescription, errorStatus, errorNumber }: HandleErrorViewType) =>
    track(trackErrorViewHandler({ errorDescription, errorStatus, errorNumber })),
});

export { KycTrackingHandlers };
